import { Cancel, CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';
import { Alert, Box, Chip, IconButton, InputAdornment, LinearProgress, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangeUserPasswordMutation } from '../../../../hooks/mutations/useUserMutation';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ModalBody from '../../ModalBody';
import { calculatePasswordStrength, getStrengthColor, getStrengthValue } from '../../../../utils/password';
import { MIN_PASSWORD_LENGTH } from '../../../../constants';

interface ModalProps {
    user: User | null;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateUserPasswordModal: React.FC<ModalProps> = ({ user, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [newPassword, setNewPassword] = useState('');
    const [newPasswordSecond, setNewPasswordSecond] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [touched, setTouched] = useState({ password: false, confirm: false });

    const { mutate: mutatePassword, isLoading } = useChangeUserPasswordMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const handleSubmit = () => {
        mutatePassword({ userId: user?._id || '', body: { newPassword } });
    };

    const handleClose = () => {
        setNewPassword('');
        setNewPasswordSecond('');
        setTouched({ password: false, confirm: false });
        setShowPassword(false);
        setShowPasswordConfirm(false);
        setShowEditModal(false);
    };

    const passwordStrength = newPassword ? calculatePasswordStrength(newPassword) : null;

    const validations = {
        minLength: newPassword.length >= MIN_PASSWORD_LENGTH,
        hasUpperCase: /[A-Z]/.test(newPassword),
        hasLowerCase: /[a-z]/.test(newPassword),
        hasNumber: /\d/.test(newPassword),
        hasSpecialChar: /[^a-zA-Z\d]/.test(newPassword),
        passwordsMatch: newPassword === newPasswordSecond && newPasswordSecond.length > 0,
    };

    const isFormValid = () => {
        return (
            validations.minLength &&
            validations.hasUpperCase &&
            validations.hasLowerCase &&
            validations.hasNumber &&
            validations.hasSpecialChar &&
            validations.passwordsMatch
        );
    };

    const ValidationItem: React.FC<{ valid: boolean; label: string }> = ({ valid, label }) => (
        <Stack direction="row" spacing={1} alignItems="center">
            {valid ? <CheckCircle fontSize="small" color="success" /> : <Cancel fontSize="small" color="disabled" />}
            <Typography variant="caption" color={valid ? 'success. main' : 'text.secondary'}>
                {label}
            </Typography>
        </Stack>
    );

    const passwordError =
        touched.password && newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH
            ? capitalizeFirstLetter(t('passwordTooShort'))
            : undefined;

    const confirmError =
        touched.confirm && newPasswordSecond.length > 0 && newPassword !== newPasswordSecond
            ? capitalizeFirstLetter(t('passwordsDontMatch'))
            : undefined;

    return (
        <ModalBody
            title={t('user')}
            subtitle={t('password')}
            description={formatName(user?.firstName, user?.lastName)}
            open={showEditModal}
            disableSubmit={!isFormValid() || isLoading}
            handleClose={handleClose}
            onSubmit={handleSubmit}
        >
            <Stack spacing={3}>
                <Alert severity="info">{t('adminPasswordChangeInfo')}</Alert>

                <Box>
                    <CustomTextInput
                        value={newPassword}
                        label={capitalizeFirstLetter(t('newPassword') || 'Nouveau mot de passe')}
                        onChange={setNewPassword}
                        onBlur={() => setTouched({ ...touched, password: true })}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        errorText={passwordError}
                        required
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />

                    {newPassword.length > 0 && (
                        <Box sx={{ mt: 1.5, px: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" color="text. secondary">
                                    {capitalizeFirstLetter(t('passwordStrength'))}:
                                </Typography>
                                <Chip
                                    label={capitalizeFirstLetter(t(passwordStrength || '') || passwordStrength || '')}
                                    size="small"
                                    color={getStrengthColor(passwordStrength) as any}
                                    sx={{ height: 20 }}
                                />
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={getStrengthValue(passwordStrength)}
                                color={getStrengthColor(passwordStrength) as any}
                                sx={{ height: 6, borderRadius: 3 }}
                            />
                        </Box>
                    )}
                </Box>

                <CustomTextInput
                    value={newPasswordSecond}
                    label={capitalizeFirstLetter(t('confirmNewPassword'))}
                    onChange={setNewPasswordSecond}
                    onBlur={() => setTouched({ ...touched, confirm: true })}
                    type={showPasswordConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    errorText={confirmError}
                    required
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} edge="end">
                                {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    }
                />

                <Box sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {capitalizeFirstLetter(t('passwordRequirements'))}:
                    </Typography>
                    <Stack spacing={0.5}>
                        <ValidationItem
                            valid={validations.minLength}
                            label={capitalizeFirstLetter(t('minLengthX', { length: MIN_PASSWORD_LENGTH }))}
                        />
                        <ValidationItem valid={validations.hasUpperCase} label={capitalizeFirstLetter(t('hasUpperCase'))} />
                        <ValidationItem valid={validations.hasLowerCase} label={capitalizeFirstLetter(t('hasLowerCase'))} />
                        <ValidationItem valid={validations.hasNumber} label={capitalizeFirstLetter(t('hasNumber'))} />
                        <ValidationItem valid={validations.hasSpecialChar} label={capitalizeFirstLetter(t('hasSpecialChar'))} />
                        {newPasswordSecond.length > 0 && (
                            <ValidationItem valid={validations.passwordsMatch} label={capitalizeFirstLetter(t('passwordsMatch'))} />
                        )}
                    </Stack>
                </Box>
            </Stack>
        </ModalBody>
    );
};

export default UpdateUserPasswordModal;
